import { VenueReservation } from "@/graphql/operations";
import useLocalization from "@/hooks/useLocalization";
import useReservation from "@/hooks/useReservation";
import {
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
} from "date-fns";
import { groupBy } from "mt-shared/collections";
import type { Ref } from "react";
import { useCallback, useMemo, useRef } from "react";
import { default as Sentinel } from "./VirtualReservationList/Sentinel";
import VirtualReservationList, {
  type VirtualReservationListHandle,
} from "./VirtualReservationList/VirtualReservationList";

type RegularReservationListProps = {
  selectedReservation: VenueReservation | null;
  selectReservation: (reservationId: string) => void;
  listRef?: Ref<VirtualReservationListHandle>;
};

export function RegularReservationList({
  selectReservation,
  selectedReservation,
  listRef,
}: RegularReservationListProps) {
  const { t } = useLocalization();
  const today = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  const hasAutoScrolledToTodayRef = useRef(false);

  const {
    reservations,
    canFetchMorePastReservations,
    canFetchMoreUpcomingReservations,
    fetchMorePastReservations,
    fetchMoreUpcomingReservations,
    loading,
    totalReservations,
    fetchedMonthsRange,
    reservationBounds,
  } = useReservation();

  const hasTriggeredTopRef = useRef(false);
  const onAtTopStateChange = useCallback(
    (isAtTop: boolean) => {
      if (!isAtTop) {
        hasTriggeredTopRef.current = false;
        return;
      }
      if (hasTriggeredTopRef.current) return;
      const shouldFetchMore = !loading && canFetchMorePastReservations;
      if (shouldFetchMore) {
        hasTriggeredTopRef.current = true;
        fetchMorePastReservations();
      }
    },
    [loading, canFetchMorePastReservations, fetchMorePastReservations],
  );

  const onEndReached = useCallback(() => {
    if (!loading && canFetchMoreUpcomingReservations)
      fetchMoreUpcomingReservations();
  }, [
    loading,
    canFetchMoreUpcomingReservations,
    fetchMoreUpcomingReservations,
  ]);

  const reservationsByDate = useMemo(() => {
    if (!fetchedMonthsRange.from || !fetchedMonthsRange.until) return [];

    const rangeStart = startOfMonth(fetchedMonthsRange.from);
    const rangeEnd = endOfMonth(fetchedMonthsRange.until);

    const days = eachDayOfInterval({ start: rangeStart, end: rangeEnd });

    const reservationsByDate = groupBy(
      reservations,
      ({ group }) => group?.date ?? "",
    );

    return days.map((day) => {
      const date = format(day, "yyyy-MM-dd");
      return {
        date,
        reservations: reservationsByDate[date] ?? [],
      };
    });
  }, [fetchedMonthsRange.from, fetchedMonthsRange.until, reservations]);

  const firstItemIndex = useMemo(() => {
    if (
      !fetchedMonthsRange.from ||
      !fetchedMonthsRange.until ||
      !reservationBounds.resolved ||
      !reservationBounds.earliestDate
    )
      return 0;

    return Math.max(
      0,
      differenceInCalendarDays(
        startOfMonth(fetchedMonthsRange.from),
        startOfDay(reservationBounds.earliestDate),
      ),
    );
  }, [
    fetchedMonthsRange.from,
    fetchedMonthsRange.until,
    reservationBounds.earliestDate,
    reservationBounds.resolved,
  ]);

  const setVirtualListRef = useCallback(
    (handle: VirtualReservationListHandle | null) => {
      if (typeof listRef === "function") {
        listRef(handle);
      } else if (listRef && "current" in listRef) {
        (listRef as { current: VirtualReservationListHandle | null }).current =
          handle;
      }

      if (!handle) return;
      if (hasAutoScrolledToTodayRef.current) return;
      hasAutoScrolledToTodayRef.current = true;
      handle.scrollToDate(today, { align: "start", behavior: "auto" });
    },
    [listRef, today],
  );

  return (
    <VirtualReservationList
      ref={setVirtualListRef}
      dayRows={reservationsByDate}
      selectedReservation={selectedReservation}
      selectReservation={selectReservation}
      atTopStateChange={onAtTopStateChange}
      endReached={onEndReached}
      firstItemIndex={firstItemIndex}
      components={{
        Header: () => (
          <Sentinel
            canFetchMoreReservations={canFetchMorePastReservations}
            totalReservations={totalReservations}
            reachedSentinelLabel={t("reservation:startOfReservations")}
            loading={loading}
          />
        ),
        Footer: () => (
          <Sentinel
            showNoReservations={true}
            canFetchMoreReservations={canFetchMoreUpcomingReservations}
            totalReservations={totalReservations}
            reachedSentinelLabel={t("reservation:endOfReservations")}
            loading={loading}
            progressProps={{ sx: { top: -4 } }}
          />
        ),
      }}
    />
  );
}
